import { _ } from "../../polyglot/polyglot.js";
import { formatAsLongMonthAndYear } from "../../helpers/date-formatting-util.js";
import { formatCurrency } from "../../helpers/currency-formatting-util.js";
import { makeHTMLToSingleLineConverter } from "../../helpers/dom-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../list/streams/ArrayStream.js";
import GridColumnSpecification from "../list/GridColumnSpecification.js";
import ListSource from "../../list/ListSource.js";
import makeTableListWidget from "../list/TableListWidget/makeTableListWidget.js";
import percentage from "../../percentage.js";
import RuleTemplatesToAssumptionsConverter from "./RuleTemplatesToAssumptionsConverter.js";
import text from "../../elements/text/text.js";
import WaitingButtonWidget from "../WaitingButtonWidget/WaitingButtonWidget.js";

/**
 * This string is shown in place of new line characters when a rule
 * template's description spans several lines:
 */
const returnSymbol = "⏎";

const convertHtmlToSingleLine = makeHTMLToSingleLineConverter({
	separator: returnSymbol,
});

/**
 * Render a collection of {@link RuleTemplate}s within a table, one
 * per row. The table contains columns such as:
 *
 * - "Account/Group" (representing the row where the rule template was added)
 * - "Start" (representing the column where the rule template was added)
 * - VAT rate (for the rule models with a VAT rate)
 * - and many other columns.
 *
 * @param {Forecast} forecast - The forecast for which the rule
 *   templates where created
 * @param {ListSource<RuleTemplate>} ruleTemplateSource - The
 *   collection of rule templates to show, one per row in the table
 * @param {function} onReportAndCellSelectionRequest - Callback to
 *   redirect the user to the corresponding tab, row and column. This
 *   is requested when clicking on an account/group name
 */
export default class ForecastAssumptionsWidget extends Widget {
	constructor({
		forecast,
		ruleTemplateSource,
		onReportAndCellSelectionRequest,
	}) {
		super(...arguments);

		this._forecast = forecast;
		this._ruleTemplateSource = ruleTemplateSource;
		this._onReportAndCellSelectionRequest = onReportAndCellSelectionRequest;
	}

	renderContentOn(html) {
		html.div(
			{ class: "ForecastAssumptionsWidget" },
			makeTableListWidget({
				source: this._convertRuleTemplateSourceToAssumptionSource(),
				columnSpecifications: this._makeColumnSpecifications(),
			}),
		);
	}

	_makeColumnSpecifications() {
		return [
			new GridColumnSpecification({
				name: _("Account/Group"),
				renderer: this._accountRenderer.bind(this),
				cssClass: "account-group",
			}),
			new GridColumnSpecification({
				name: _("Start"),
				renderer: this._startRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("End"),
				renderer: this._endRenderer.bind(this),
				dataTestName: "end",
			}),
			new GridColumnSpecification({
				name: _("Name"),
				renderer: this._nameRenderer.bind(this),
				dataTestName: "name",
				cssClass: "name",
			}),
			new GridColumnSpecification({
				name: _("Rule type"),
				renderer: this._ruleTypeRenderer.bind(this),
			}),
			new GridColumnSpecification({
				name: _("Value"),
				renderer: this._valueRenderer.bind(this),
				dataTestName: "value",
			}),
			new GridColumnSpecification({
				name: _("Historical input"),
				renderer: this._historicalInputRenderer.bind(this),
				dataTestName: "historical-input",
			}),
			new GridColumnSpecification({
				name: _("Increase/Decrease"),
				renderer: this._growthPercentageRenderer.bind(this),
				dataTestName: "growth-percentage",
			}),
			new GridColumnSpecification({
				name: _("VAT Rate"),
				renderer: this._vatRenderer.bind(this),
				dataTestName: "vat-rate",
			}),
			new GridColumnSpecification({
				name: _("Payment spread"),
				renderer: this._paymentSpreadRenderer.bind(this),
				dataTestName: "payment-spread",
			}),
			new GridColumnSpecification({
				name: _("Description"),
				renderer: this._descriptionRenderer.bind(this),
				dataTestName: "description",
				cssClass: "description",
			}),
		];
	}

	_accountRenderer(assumption) {
		const cell = assumption.findCell({ forecast: this._forecast });
		const label = this._makeAccountLabel({ assumption, row: cell.row });

		return new WaitingButtonWidget({
			label,
			waitingLabel: label,
			isAnchor: true,
			onClick: () =>
				new Promise((resolve) =>
					// Makes sure the anchor has time to update its loading state
					setTimeout(() => {
						this._onReportAndCellSelectionRequest(cell);

						resolve();
					}, 0),
				),
		});
	}

	_makeAccountLabel({ assumption, row }) {
		let account = assumption.getAccount();

		if (!account) {
			if (row.isAccountRow()) {
				account = row.getAccount();
			} else {
				return row.getTitle();
			}
		}

		return (html) =>
			html.div(
				text({ emphasize: true }, account.getNumber()),
				" ",
				account.getName(),
			);
	}

	_startRenderer(assumption) {
		const start = assumption.getStart();
		const firstPredictedPeriod = this._forecast.getPredictedPeriods()[0];

		const periodToDisplay = firstPredictedPeriod
			? firstPredictedPeriod.max(start)
			: start;

		return formatAsLongMonthAndYear(periodToDisplay.asDate());
	}

	_endRenderer(assumption) {
		const end = assumption.getEnd();

		if (end === Infinity) {
			return _("N/A");
		}

		if (!end) {
			return () => {};
		}

		return formatAsLongMonthAndYear(end.asDate());
	}

	_valueRenderer(assumption) {
		const value = assumption.getValue();

		return value ? formatCurrency(value) : "";
	}

	_growthPercentageRenderer(assumption) {
		return assumption.getGrowthPercentage() || "";
	}

	_nameRenderer(assumption) {
		return assumption.getName() || "";
	}

	_ruleTypeRenderer(assumption) {
		return assumption.getRuleModelLabel();
	}

	_vatRenderer(assumption) {
		return assumption.getVatRateValue() || "";
	}

	_historicalInputRenderer(assumption) {
		return assumption.getHistoricalInput() || "";
	}

	_paymentSpreadRenderer(assumption) {
		const paymentSpread = assumption.getPaymentSpread();

		if (!paymentSpread) return "";

		return paymentSpread
			.map((amount) => percentage(amount / 100))
			.join(" | ");
	}

	_descriptionRenderer(assumption) {
		// We don't want long descriptions with several paragraphs to
		// impact the visual aspect of the table so we shorten the
		// description to just one line.
		return convertHtmlToSingleLine(assumption.getDescription());
	}

	_convertRuleTemplateSourceToAssumptionSource() {
		return ListSource.makeAndFetch({
			stream: new ArrayStream({
				data: this._ruleTemplateSource
					.fetch()
					.then(({ items: ruleTemplates, cancelled }) => {
						if (cancelled) return [];

						const assumptions =
							RuleTemplatesToAssumptionsConverter.convert({
								ruleTemplates,
							});

						return [...this._sortAssumptions(assumptions)];
					}),
			}),
		});
	}

	*_sortAssumptions(assumptions) {
		const rows = this._getAllRowsOfForecast(this._forecast);
		const assumptionsByRowId = this._groupAssumptionsByRowId(assumptions);

		for (const row of rows) {
			const assumptionsInRow = assumptionsByRowId.get(row.getStableId());
			if (!assumptionsInRow) continue;

			// assumptions on the same row are sorted by start date:
			yield* assumptionsInRow.sort((assumption1, assumption2) =>
				assumption1.getStart().compareTo(assumption2.getStart()),
			);
		}
	}

	_groupAssumptionsByRowId(assumptions) {
		const result = new Map();

		for (const assumption of assumptions) {
			let assumptionsInSameRow = result.get(assumption.getRowId());

			if (!assumptionsInSameRow) {
				assumptionsInSameRow = [];
				result.set(assumption.getRowId(), assumptionsInSameRow);
			}

			assumptionsInSameRow.push(assumption);
		}

		return result;
	}

	*_getAllRowsOfForecast(forecast) {
		for (const report of forecast.getReportsAsList()) {
			yield* this._getAllRowsOfRows(report.getRows());
		}
	}

	*_getAllRowsOfRows(rows) {
		for (const row of rows) {
			yield* this._getAllRowsOfRow(row);
		}
	}

	*_getAllRowsOfRow(row) {
		if (!row) return;

		yield row;
		yield* this._getAllRowsOfRows(row.getRows());
	}
}
